import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MagnifyingGlass, Sparkle } from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";

export default function MarketTrends() {
  const [form, setForm] = useState({
    zip_code: "78704",
    min_price: 1500,
    max_price: 3000,
    property_type: "apartment",
    search_preferences: "2 bed, pet friendly, near downtown",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/market/research", {
        zip_code: form.zip_code,
        min_price: Number(form.min_price),
        max_price: Number(form.max_price),
        property_type: form.property_type,
        search_preferences: form.search_preferences,
      });
      setResult(data.result);
    } catch (err) { toast.error(err?.response?.data?.detail || "Research failed"); }
    setLoading(false);
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="market-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Market Research</div>
        <h1 className="font-heading text-4xl font-semibold text-stone-900 mt-1">Trend Signals by Zip Code</h1>
        <p className="text-stone-500 mt-1">
          Get AI-synthesized insights on keywords, demographics, and pricing trends.
          <span className="ml-1 text-stone-400">(Zillow API integration coming soon.)</span>
        </p>
      </div>

      <Card className="p-6 border-stone-200 rounded-xl shadow-none bg-white mb-6">
        <form onSubmit={run} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end" data-testid="market-form">
          <div className="md:col-span-1">
            <Label>Zip code</Label>
            <Input value={form.zip_code} onChange={onChange("zip_code")} data-testid="market-zip" />
          </div>
          <div className="md:col-span-1">
            <Label>Min ($)</Label>
            <Input type="number" value={form.min_price} onChange={onChange("min_price")} data-testid="market-min" />
          </div>
          <div className="md:col-span-1">
            <Label>Max ($)</Label>
            <Input type="number" value={form.max_price} onChange={onChange("max_price")} data-testid="market-max" />
          </div>
          <div className="md:col-span-1">
            <Label>Type</Label>
            <Input value={form.property_type} onChange={onChange("property_type")} data-testid="market-type" />
          </div>
          <div className="md:col-span-2">
            <Label>Preferences</Label>
            <Input value={form.search_preferences} onChange={onChange("search_preferences")} data-testid="market-prefs" />
          </div>
          <div className="md:col-span-6">
            <Button type="submit" disabled={loading} className="bg-forest-600 hover:bg-forest-500 text-white" data-testid="market-run">
              <MagnifyingGlass size={16} className="mr-2" /> {loading ? "Researching…" : "Run AI research"}
            </Button>
          </div>
        </form>
      </Card>

      {!result && !loading && (
        <Card className="p-10 text-center border-stone-200 shadow-none rounded-xl bg-white">
          <Sparkle size={28} className="mx-auto text-forest-500" weight="duotone" />
          <div className="font-heading text-xl mt-3">Run a search to see insights</div>
          <div className="text-stone-500 text-sm mt-1">Try different zip codes to compare demand signals.</div>
        </Card>
      )}

      {result && (
        <div className="space-y-6 animate-fade-in-up" data-testid="market-result">
          <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Overview</div>
            <p className="mt-2 text-stone-700 leading-relaxed">{result.overview}</p>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
              <h3 className="font-heading text-xl mb-4">Trending keywords</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={result.trending_keywords} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis type="number" stroke="#A8A29E" />
                    <YAxis type="category" dataKey="keyword" stroke="#A8A29E" width={110} />
                    <Tooltip />
                    <Bar dataKey="score" fill="#2D6A4F" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
              <h3 className="font-heading text-xl mb-4">6-month price trend</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.monthly_price_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis dataKey="month" stroke="#A8A29E" />
                    <YAxis stroke="#A8A29E" />
                    <Tooltip />
                    <Line type="monotone" dataKey="price" stroke="#E07A5F" strokeWidth={3} dot={{ r: 4, fill: "#E07A5F" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
              <h3 className="font-heading text-xl mb-4">Price insights</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Stat k="Median" v={`$${result.price_insights?.median}`} />
                <Stat k="Min" v={`$${result.price_insights?.min}`} />
                <Stat k="Max" v={`$${result.price_insights?.max}`} />
                <Stat k="Trend" v={result.price_insights?.trend} />
              </div>
            </Card>
            <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
              <h3 className="font-heading text-xl mb-4">Demographics</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Stat k="Median Age" v={result.demographics?.median_age} />
                <Stat k="Median Income" v={result.demographics?.median_income} />
                <Stat k="Household Size" v={result.demographics?.household_size} />
                <Stat k="Education" v={result.demographics?.education} />
              </div>
            </Card>
          </div>

          <Card className="p-6 border-stone-200 shadow-none rounded-xl bg-white">
            <h3 className="font-heading text-xl mb-4">Demand signals</h3>
            <ul className="space-y-2">
              {(result.demand_signals || []).map((s, i) => (
                <li key={i} className="flex gap-3 text-sm text-stone-700">
                  <span className="text-forest-500 mt-1">●</span>{s}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.18em] text-stone-400">{k}</div>
    <div className="font-heading text-lg font-medium text-stone-900">{v ?? "—"}</div>
  </div>
);
